/**
 * Shared table pagination — default 20 rows, then pager arrows.
 * Screens should import these instead of local PAGE_SIZE constants.
 */

/** Default rows per page for every paged table unless the user opts into another size. */
export const DEFAULT_PAGE_SIZE = 20

/** Common page-size choices for selects (always includes DEFAULT_PAGE_SIZE). */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number]

export function pageCountOf(total: number, pageSize: number = DEFAULT_PAGE_SIZE): number {
  const size = Math.max(1, pageSize)
  const n = Math.max(0, total)
  return Math.max(1, Math.ceil(n / size) || 1)
}

export function clampPage(page: number, pageCount: number): number {
  const count = Math.max(1, pageCount)
  return Math.min(Math.max(1, page), count)
}

/** Zero-based slice of `items` for the 1-based `page`. */
export function slicePage<T>(
  items: readonly T[],
  page: number,
  pageSize: number = DEFAULT_PAGE_SIZE,
): T[] {
  const size = Math.max(1, pageSize)
  const count = pageCountOf(items.length, size)
  const safe = clampPage(page, count)
  const from = (safe - 1) * size
  return items.slice(from, from + size)
}

export function pageRangeMeta(
  page: number,
  pageSize: number,
  total: number,
): { first: number; last: number } | null {
  if (total <= 0) return null
  const size = Math.max(1, pageSize)
  const count = pageCountOf(total, size)
  const safe = clampPage(page, count)
  const first = (safe - 1) * size + 1
  const last = Math.min(total, safe * size)
  return { first, last }
}
